import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { sanitizeHtml } from '../utils/sanitize';

// Fix Leaflet icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Region { id: number; name_ar: string; }
interface City { id: number; name_ar: string; }

// Map click handler
const LocationPicker: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Step indicator
const StepIndicator: React.FC<{ current: number; total: number; labels: string[] }> = ({ current, total, labels }) => (
  <div className="flex items-center justify-center mb-8">
    {labels.map((label, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            i + 1 <= current ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-400'
          }`}>
            {i + 1 <= current - 1 ? '✓' : i + 1}
          </div>
          <span className={`text-[10px] mt-1 ${i + 1 <= current ? 'text-primary-500 font-semibold' : 'text-gray-400'}`}>{label}</span>
        </div>
        {i < total - 1 && (
          <div className={`w-12 h-0.5 mx-1 mt-[-12px] ${i + 1 < current ? 'bg-primary-500' : 'bg-gray-200'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ===== Step 1: بيانات الحساب (الجوال + الإيميل + كلمة المرور) =====
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // ===== Step 2: التحقق =====
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendIn, setResendIn] = useState(0);
  // قناة استلام رمز التحقق (تُقرأ من /app-config)
  const [otpEmailEnabled, setOtpEmailEnabled] = useState(true);
  const [otpSmsEnabled, setOtpSmsEnabled] = useState(false);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [channelUsed, setChannelUsed] = useState<'email' | 'sms'>('email');

  // ===== Step 3: إكمال البيانات =====
  const [name, setName] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // خيارات إضافية (اختيارية)
  const [showExtras, setShowExtras] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [requestVerification, setRequestVerification] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentImage, setDocumentImage] = useState<File | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string>('');

  // نافذة الشروط/الخصوصية المنبثقة
  const [policyModal, setPolicyModal] = useState<string | null>(null);
  const [policyData, setPolicyData] = useState<{ title_ar: string; content_ar: string } | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);

  const fullPhone = () => (phone.startsWith('0') ? phone : `0${phone}`);

  const openPolicy = (slug: string) => {
    setPolicyModal(slug);
    setPolicyData(null);
    setPolicyLoading(true);
    api.get('/pages/' + slug)
      .then((res) => setPolicyData(res.data.data))
      .catch(() => setPolicyData(null))
      .finally(() => setPolicyLoading(false));
  };

  // قنوات OTP المفعّلة — تحدّد إظهار المُنتقي والقناة الافتراضية
  useEffect(() => {
    api.get('/app-config').then((res) => {
      const otp = res.data?.data?.otp || {};
      const e = otp.email_enabled !== false;
      const sm = !!otp.sms_enabled;
      setOtpEmailEnabled(e);
      setOtpSmsEnabled(sm);
      setChannel(e ? 'email' : (sm ? 'sms' : 'email'));
    }).catch(() => {});
  }, []);

  // Fetch regions
  useEffect(() => {
    api.get('/regions').then((res) => setRegions(res.data.data || [])).catch(() => {});
  }, []);

  // Fetch cities on region change
  useEffect(() => {
    if (regionId) {
      api.get(`/regions/${regionId}/cities`).then((res) => setCities(res.data.data || [])).catch(() => setCities([]));
    } else {
      setCities([]);
      setCityId('');
    }
  }, [regionId]);

  // عدّاد إعادة الإرسال
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // استخراج أول رسالة خطأ من الاستجابة
  const firstErrorMessage = (err: any, fallback = 'حدث خطأ') => {
    const errors = err?.response?.data?.errors;
    if (errors) {
      const first = Object.values(errors)[0];
      return Array.isArray(first) ? (first[0] as string) : String(first);
    }
    return err?.response?.data?.message || fallback;
  };

  // ===== الخطوة 1: التحقق من الصيغة ثم إرسال الرمز =====
  const validateAccount = (): boolean => {
    if (!email || !phone || !password) { toast.error('أكمل البريد والجوال وكلمة المرور'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('البريد الإلكتروني غير صحيح'); return false; }
    if (!/^05[0-9]{8}$/.test(fullPhone())) { toast.error('رقم الجوال غير صحيح (مثال: 5XXXXXXXX)'); return false; }
    if (password.length < 6) { toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return false; }
    if (password !== passwordConfirm) { toast.error('كلمة المرور غير متطابقة'); return false; }
    return true;
  };

  const sendCode = async () => {
    if (!validateAccount()) return;
    try {
      setLoading(true);
      const res = await api.post('/auth/register/send-code', { email, phone: fullPhone(), channel });
      const d = res.data.data || {};
      const used = (d.channel === 'sms' || d.channel === 'email') ? d.channel : channel;
      setChannelUsed(used);
      setMaskedEmail(d.masked_email || '');
      setResendIn(60);
      setCode('');
      setStep(2);
      toast.success(used === 'sms' ? 'تم إرسال رمز التحقق إلى جوالك' : 'تم إرسال رمز التحقق إلى بريدك');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        const wait = err?.response?.data?.errors?.wait_seconds;
        toast.error(wait ? `انتظر ${wait} ثانية قبل إعادة الإرسال` : firstErrorMessage(err));
        if (wait) setResendIn(Number(wait));
      } else {
        toast.error(firstErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendIn > 0) return;
    try {
      setLoading(true);
      const res = await api.post('/auth/register/send-code', { email, phone: fullPhone(), channel });
      const d = res.data.data || {};
      const used = (d.channel === 'sms' || d.channel === 'email') ? d.channel : channel;
      setChannelUsed(used);
      setMaskedEmail(d.masked_email || '');
      setResendIn(60);
      toast.success('تم إرسال الرمز مرة أخرى');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        const wait = err?.response?.data?.errors?.wait_seconds;
        if (wait) setResendIn(Number(wait));
        toast.error(wait ? `انتظر ${wait} ثانية` : firstErrorMessage(err));
      } else {
        toast.error(firstErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // ===== الخطوة 2: التحقق من الرمز =====
  const verifyCode = async () => {
    if (!code || code.length < 4) { toast.error('أدخل رمز التحقق'); return; }
    try {
      setLoading(true);
      await api.post('/auth/register/verify-code', { email, phone: fullPhone(), code });
      toast.success('تم التحقق، أكمل بياناتك');
      setStep(3);
    } catch (err: any) {
      toast.error(firstErrorMessage(err, 'رمز غير صحيح'));
    } finally {
      setLoading(false);
    }
  };

  // ===== الخطوة 3: أدوات مساعدة =====
  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('المتصفح لا يدعم تحديد الموقع'); return; }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setGettingLocation(false);
        toast.success('تم تحديد موقعك');
      },
      () => {
        setGettingLocation(false);
        toast.error('فشل تحديد الموقع، حدد يدوياً على الخريطة');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocumentImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setDocumentPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const validateProfile = (): boolean => {
    if (!name || name.trim().length < 2) { toast.error('أدخل الاسم'); return false; }
    if (!cityId) { toast.error('اختر المدينة'); return false; }
    if (requestVerification && !documentType) { toast.error('اختر نوع المستند'); return false; }
    if (requestVerification && !documentImage) { toast.error('ارفع صورة المستند'); return false; }
    if (!termsAccepted) { toast.error('يجب الموافقة على الشروط والأحكام'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateProfile()) return;
    try {
      setLoading(true);
      const res = await api.post('/auth/register', {
        name,
        email,
        phone: fullPhone(),
        password,
        password_confirmation: passwordConfirm,
        city_id: cityId,
        terms_accepted: '1',
      });

      const { token, user } = res.data.data;
      login(token, user);

      // خيارات اختيارية بعد إنشاء الحساب (لا تُعطّل النجاح إن فشلت)
      if (latitude && longitude) {
        try { await api.put('/profile', { latitude, longitude }); } catch (e) { /* ignore */ }
      }

      if (requestVerification && documentImage) {
        try {
          const vfd = new FormData();
          vfd.append('document_type', documentType);
          vfd.append('document_image', documentImage);
          await api.post('/profile/verify', vfd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.success('تم إنشاء حسابك! طلب التوثيق قيد المراجعة', { duration: 5000 });
        } catch (e) {
          toast.success(`أهلاً ${user.name}! تم إنشاء حسابك`);
          toast.error('تعذّر إرسال طلب التوثيق، أعد المحاولة من ملفك الشخصي');
        }
      } else {
        toast.success(`أهلاً ${user.name}! تم إنشاء حسابك بنجاح`);
      }

      navigate('/');
    } catch (err: any) {
      toast.error(firstErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 w-full max-w-lg">
        <div className="text-center mb-4">
          <img src="/aljalab-logo.png" alt="الجلب" className="mx-auto mb-2" style={{ height: 48, width: "auto" }} />
          <p className="text-gray-500 text-sm">إنشاء حساب جديد</p>
        </div>

        <StepIndicator current={step} total={3} labels={['الحساب', 'التحقق', 'إكمال البيانات']} />

        {/* ========== Step 1: بيانات الحساب ========== */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">البريد الإلكتروني *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@email.com"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" dir="ltr" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">رقم الجوال *</label>
              <div className="flex gap-2" dir="ltr">
                <span className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-3 text-sm text-gray-500">+966</span>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="5XXXXXXXX"
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" maxLength={10} dir="ltr" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">كلمة المرور *</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="كلمة المرور"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">تأكيد المرور *</label>
                <input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="أعد الكتابة"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            {otpEmailEnabled && otpSmsEnabled && (
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">استلام رمز التحقق عبر</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setChannel('email')}
                    className={`border rounded-lg px-4 py-3 text-sm font-semibold transition ${channel === 'email' ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-500'}`}>
                    ✉️ الإيميل
                  </button>
                  <button type="button" onClick={() => setChannel('sms')}
                    className={`border rounded-lg px-4 py-3 text-sm font-semibold transition ${channel === 'sms' ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-500'}`}>
                    📱 الجوال
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-400 text-center pt-1">
              {channel === 'sms'
                ? 'بنرسل لك رمز تحقق على جوالك للتأكد أنه لك قبل إكمال التسجيل'
                : 'بنرسل لك رمز تحقق على بريدك للتأكد أنه لك قبل إكمال التسجيل'}
            </p>
          </div>
        )}

        {/* ========== Step 2: التحقق ========== */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="text-center">
              <span className="text-4xl">📩</span>
              <p className="text-sm text-gray-600 mt-3">أدخل رمز التحقق المرسل إلى</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5" dir="ltr">{channelUsed === 'sms' ? fullPhone() : (maskedEmail || email)}</p>
            </div>

            <div>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="------"
                dir="ltr"
                className="w-full border border-gray-200 rounded-lg px-4 py-4 text-center text-2xl tracking-[0.5em] font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                maxLength={6}
              />
            </div>

            <div className="text-center">
              {resendIn > 0 ? (
                <p className="text-xs text-gray-400">يمكنك إعادة الإرسال بعد {resendIn} ثانية</p>
              ) : (
                <button type="button" onClick={resendCode} disabled={loading}
                  className="text-sm text-primary-500 font-semibold hover:underline disabled:opacity-50">
                  إعادة إرسال الرمز
                </button>
              )}
            </div>

            {channelUsed === 'email' && (
              <p className="text-center text-[11px] text-gray-400">
                ما وصلك الرمز؟ تحقق من مجلد الرسائل غير المرغوب فيها (Spam)
              </p>
            )}
          </div>
        )}

        {/* ========== Step 3: إكمال البيانات ========== */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">الاسم *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسمك الكامل"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-600">
                تسجّل كـ<span className="font-semibold text-primary-600">عضو</span> — تتصفّح وتشتري وتراسل البائعين.
                وإذا حبيت تبيع، ترقّي حسابك لبائع لاحقاً بضغطة.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">المنطقة *</label>
                <select value={regionId} onChange={(e) => setRegionId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">اختر المنطقة</option>
                  {regions.map((r) => <option key={r.id} value={r.id}>{r.name_ar}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">المدينة *</label>
                <select value={cityId} onChange={(e) => setCityId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">اختر المدينة</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
                </select>
              </div>
            </div>

            {/* خيارات إضافية اختيارية */}
            <div className="border-t pt-3">
              <button type="button" onClick={() => setShowExtras((v) => !v)}
                className="w-full flex items-center justify-between text-sm text-gray-600 font-semibold py-1">
                <span>خيارات إضافية (اختياري)</span>
                <span className={`transition-transform ${showExtras ? 'rotate-180' : ''}`}>▾</span>
              </button>

              {showExtras && (
                <div className="space-y-4 pt-3">
                  {/* الموقع */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">📍 موقعك (يسهّل على المشترين إيجادك)</p>
                    <button type="button" onClick={getCurrentLocation} disabled={gettingLocation}
                      className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-primary-500 text-primary-500 rounded-lg text-sm font-semibold hover:bg-primary-50 transition disabled:opacity-50 mb-2">
                      {gettingLocation ? <span>جاري تحديد الموقع...</span> : <span>استخدم موقعي الحالي</span>}
                    </button>
                    <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '220px' }}>
                      <MapContainer
                        center={latitude && longitude ? [latitude, longitude] : [24.7136, 46.6753]}
                        zoom={latitude ? 13 : 6}
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={true}
                      >
                        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <LocationPicker onLocationSelect={(lat, lng) => { setLatitude(lat); setLongitude(lng); }} />
                        {latitude && longitude && <Marker position={[latitude, longitude]} />}
                      </MapContainer>
                    </div>
                    {latitude && longitude && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center mt-2">
                        <p className="text-[11px] text-green-600 font-semibold" dir="ltr">✓ {latitude.toFixed(4)}, {longitude.toFixed(4)}</p>
                      </div>
                    )}
                  </div>

                  {/* التوثيق */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🛡️</span>
                      <div>
                        <h3 className="font-bold text-gray-800 text-sm">توثيق الحساب</h3>
                        <p className="text-xs text-gray-500 mt-1">الحساب الموثق يحصل على ثقة أكبر وشارة التوثيق الزرقاء</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button type="button" onClick={() => setRequestVerification(true)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition ${requestVerification ? 'border-primary-500 bg-primary-50 text-primary-500' : 'border-gray-200 text-gray-500'}`}>
                        أرغب بالتوثيق
                      </button>
                      <button type="button" onClick={() => { setRequestVerification(false); setDocumentType(''); setDocumentImage(null); setDocumentPreview(''); }}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition ${!requestVerification ? 'border-gray-400 bg-gray-50 text-gray-600' : 'border-gray-200 text-gray-500'}`}>
                        لاحقاً
                      </button>
                    </div>

                    {requestVerification && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">نوع المستند *</label>
                          <select value={documentType} onChange={(e) => setDocumentType(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                            <option value="">اختر نوع المستند</option>
                            <option value="national_id">الهوية الوطنية</option>
                            <option value="commercial_register">السجل التجاري</option>
                            <option value="freelance">وثيقة العمل الحر</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">صورة المستند *</label>
                          <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-primary-300 transition cursor-pointer"
                            onClick={() => document.getElementById('doc-upload')?.click()}>
                            {documentPreview ? (
                              <div>
                                <img src={documentPreview} alt="preview" className="max-h-32 mx-auto rounded-lg mb-2" />
                                <p className="text-xs text-green-600 font-semibold">✓ تم رفع المستند</p>
                              </div>
                            ) : (
                              <div>
                                <span className="text-3xl">📄</span>
                                <p className="text-sm text-gray-500 mt-2">اضغط لرفع صورة المستند</p>
                                <p className="text-[10px] text-gray-400 mt-1">JPG, PNG — حد أقصى 5 ميقا</p>
                              </div>
                            )}
                          </div>
                          <input id="doc-upload" type="file" accept="image/*" onChange={handleDocumentChange} className="hidden" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* الشروط */}
            <div className="border-t pt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-primary-500 border-gray-300 rounded focus:ring-primary-500" />
                <span className="text-sm text-gray-600">
                  أوافق على{' '}
                  <button type="button" onClick={() => openPolicy('terms')} className="text-primary-500 underline">الشروط والأحكام</button>
                  {' '}و{' '}
                  <button type="button" onClick={() => openPolicy('privacy')} className="text-primary-500 underline">سياسة الخصوصية</button>
                </span>
              </label>
            </div>
          </div>
        )}

        {/* ========== أزرار التنقل ========== */}
        <div className="flex gap-3 mt-6">
          {step === 1 && (
            <button type="button" onClick={sendCode} disabled={loading}
              className="flex-1 py-3 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition disabled:opacity-50">
              {loading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
            </button>
          )}

          {step === 2 && (
            <>
              <button type="button" onClick={() => setStep(1)}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-lg font-semibold text-sm hover:bg-gray-50 transition">
                تعديل البيانات
              </button>
              <button type="button" onClick={verifyCode} disabled={loading}
                className="flex-1 py-3 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition disabled:opacity-50">
                {loading ? 'جاري التحقق...' : 'تأكيد'}
              </button>
            </>
          )}

          {step === 3 && (
            <button type="button" onClick={handleSubmit} disabled={loading}
              className="flex-1 py-3 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition disabled:opacity-50">
              {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
            </button>
          )}
        </div>

        <p className="text-center text-sm text-gray-400 mt-5">
          لديك حساب؟{' '}
          <Link to="/login" className="text-primary-500 font-semibold">تسجيل الدخول</Link>
        </p>
      </div>

      {/* نافذة الشروط/الخصوصية المنبثقة */}
      {policyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPolicyModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()} dir="rtl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg text-gray-900">
                {policyData?.title_ar || (policyModal === 'terms' ? 'الشروط والأحكام' : 'سياسة الخصوصية')}
              </h3>
              <button type="button" onClick={() => setPolicyModal(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-1">×</button>
            </div>
            <div className="p-5 overflow-y-auto">
              {policyLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent" />
                </div>
              ) : policyData ? (
                <div className="text-gray-700 leading-relaxed text-sm" style={{ direction: 'rtl' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(policyData.content_ar) }} />
              ) : (
                <p className="text-gray-500 text-center py-8">تعذّر تحميل المحتوى، حاول لاحقاً.</p>
              )}
            </div>
            <div className="p-4 border-t text-center">
              <button type="button" onClick={() => setPolicyModal(null)}
                className="px-8 py-2.5 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition">
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
